import { useEffect, useMemo, useState } from 'react';
import {
  getRecaptchaAction,
  isRecaptchaEnabled,
  isTemporaryRecaptchaError,
  requestLoginRecaptchaToken,
  verifyLoginRecaptchaToken
} from '../services/recaptcha';

const MAX_ATTEMPTS = 3;
const LOCK_DURATION_MS = 30_000;

function LoginPanel({ onAuthenticate, externalError, recaptcha = {} }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockUntil, setLockUntil] = useState(null);
  const [now, setNow] = useState(Date.now());
  const recaptchaApi = {
    getAction: recaptcha.getAction || getRecaptchaAction,
    isEnabled: recaptcha.isEnabled || isRecaptchaEnabled,
    requestToken: recaptcha.requestToken || requestLoginRecaptchaToken,
    verifyToken: recaptcha.verifyToken || verifyLoginRecaptchaToken,
    isTemporaryError: recaptcha.isTemporaryError || isTemporaryRecaptchaError
  };

  const isLocked = Boolean(lockUntil && lockUntil > now);
  const remainingSeconds = useMemo(() => {
    if (!isLocked || !lockUntil) return 0;
    return Math.max(0, Math.ceil((lockUntil - now) / 1000));
  }, [isLocked, lockUntil, now]);

  useEffect(() => {
    if (!isLocked) return undefined;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => clearInterval(timer);
  }, [isLocked]);

  useEffect(() => {
    if (!isLocked) return undefined;
    const timer = setTimeout(() => {
      setLockUntil(null);
    }, Math.max(0, lockUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [isLocked, lockUntil]);

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (isLocked || loading) return;

    try {
      setLoading(true);
      setError('');

      if (recaptchaApi.isEnabled()) {
        const action = recaptchaApi.getAction();
        const recaptchaToken = await recaptchaApi.requestToken({ action });
        await recaptchaApi.verifyToken({
          token: recaptchaToken,
          action
        });
      }

      await onAuthenticate(form.email, form.password);
    } catch (authError) {
      if (!recaptchaApi.isTemporaryError(authError)) {
        const nextAttempts = attempts + 1;
        setAttempts(nextAttempts);

        if (nextAttempts >= MAX_ATTEMPTS) {
          setLockUntil(Date.now() + LOCK_DURATION_MS);
          setAttempts(0);
        }
      }

      setError(authError?.message || 'No fue posible iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <form className="login-card" onSubmit={handleSubmit}>
        <div>
          <p className="eyebrow">Control de acceso</p>
          <h1>Inicia sesión para ver el tablero</h1>
          <p className="subtitle">
            El acceso se limita a operadores autorizados de AC Negocios. Tus credenciales se validan
            con Firebase Authentication y nunca se almacenan en este navegador.
          </p>
        </div>

        <label>
          Correo electrónico
          <input
            type="email"
            name="email"
            autoComplete="username"
            value={form.email}
            onChange={handleChange}
            disabled={loading || isLocked}
            required
          />
        </label>

        <label>
          Contraseña
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            value={form.password}
            onChange={handleChange}
            disabled={loading || isLocked}
            required
            minLength={8}
          />
        </label>

        {error && <p className="helper-text error">{error}</p>}
        {isLocked && (
          <p className="helper-text warning">
            Por seguridad bloqueamos nuevos intentos. Reintenta en {remainingSeconds} segundos.
          </p>
        )}
        {externalError && !isLocked && (
          <p className="helper-text error">{externalError}</p>
        )}

        <div className="form-actions">
          <button className="btn primary" type="submit" disabled={loading || isLocked}>
            {loading ? 'Validando...' : 'Ingresar'}
          </button>
        </div>

        <div className="auth-footnote">
          {recaptchaApi.isEnabled() && (
            <p>
              <strong>reCAPTCHA Enterprise activo.</strong> La validación corre en silencio antes de Firebase Auth.
            </p>
          )}
          <p>¿Necesitas acceso? Solicitalo con el administrador del proyecto en Firebase Console.</p>
        </div>
      </form>
    </div>
  );
}

export default LoginPanel;
